import { useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import CTASection from "@/components/shared/CTASection";
import LastReviewedBanner from "@/components/shared/LastReviewedBanner";
import { OPEN_DATA_GROUPS, PORTFOLIO_LAST_REVIEWED } from "@/data/content";

const FILTERS = [{ slug: "all", label: "All" }, ...OPEN_DATA_GROUPS.map((g) => ({ slug: g.slug, label: g.label }))];

export default function OpenDataSources() {
  const [filter, setFilter] = useState("all");

  const visibleGroups = useMemo(
    () => (filter === "all" ? OPEN_DATA_GROUPS : OPEN_DATA_GROUPS.filter((g) => g.slug === filter)),
    [filter]
  );

  const totalCount = OPEN_DATA_GROUPS.reduce((n, g) => n + g.sources.length, 0);

  return (
    <>
      <PageHeader
        overline="Open Data Sources"
        title="A curated registry of authoritative open datasets."
        lead={`A working library of ${totalCount} open data sources spanning climate, disaster risk, earth observation, environment, floods, and hydrology — vetted for resilience decisions worldwide.`}
      />

      <section className="ccdr-section">
        <div className="ccdr-container">
          <LastReviewedBanner
            date={PORTFOLIO_LAST_REVIEWED}
            source="Adapted from the ICPAC open-data registry"
          />

          {/* Filter chips */}
          <div className="flex flex-wrap items-center gap-2 mb-12">
            {FILTERS.map((f) => {
              const active = filter === f.slug;
              const count = f.slug === "all"
                ? totalCount
                : OPEN_DATA_GROUPS.find((g) => g.slug === f.slug).sources.length;
              return (
                <button
                  key={f.slug}
                  data-testid={`open-data-filter-${f.slug}`}
                  onClick={() => setFilter(f.slug)}
                  className={`px-4 py-2 text-sm border transition-colors ${
                    active
                      ? "bg-[#0B4F6C] text-white border-[#0B4F6C]"
                      : "bg-white text-[#111827] border-[#E5E7EB] hover:border-[#0B4F6C]"
                  }`}
                >
                  {f.label} <span className="opacity-70">({count})</span>
                </button>
              );
            })}
          </div>

          {/* Grouped source cards */}
          {visibleGroups.map((g) => (
            <div key={g.slug} className="mb-16">
              <p className="ccdr-eyebrow mb-6">{g.label} · {g.sources.length} sources</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
                {g.sources.map((s) => (
                  <a
                    key={s.url}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`open-data-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 50)}`}
                    className="bg-white p-7 flex flex-col group hover:bg-[#FAFAFA] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h3
                        className="text-base group-hover:text-[var(--ccdr-primary)] transition-colors"
                        style={{ color: "var(--ccdr-primary-dark)", fontWeight: 500 }}
                      >
                        {s.name}
                      </h3>
                      <ExternalLink
                        className="w-4 h-4 flex-shrink-0 mt-1 opacity-50 group-hover:opacity-100 transition-opacity"
                        style={{ color: "var(--ccdr-accent-dark)" }}
                      />
                    </div>
                    <p className="text-sm text-[#4B5563] flex-1">{s.desc}</p>
                    <span
                      className="mt-4 text-[10px] uppercase tracking-[0.22em] truncate"
                      style={{ color: "var(--ccdr-text-muted)" }}
                    >
                      {(() => {
                        try {
                          return new URL(s.url).host;
                        } catch {
                          return s.url;
                        }
                      })()}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          ))}

          <div className="ccdr-callout mt-12 max-w-3xl">
            <p className="ccdr-prose">
              <strong>A note on independence.</strong>{" "}
              The Centre links to these sources as authoritative open data, with thanks to ICPAC for the registry
              foundation. Listing does not imply that the source endorses the Centre, nor that the Centre verifies the
              currency of every linked dataset. Please cite each source directly when using it.
            </p>
          </div>
        </div>
      </section>

      <CTASection
        title="Propose a source, or invite the Centre to convene around the data."
        primary={{ to: "/get-involved", label: "Get in touch" }}
        secondary={{ to: "/foresight", label: "Or open the Foresight Observatory" }}
      />
    </>
  );
}
